import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { reportsAPI, productsAPI, ordersAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { DollarSign, ShoppingCart, Users, TrendingUp, Package, ArrowRight, Plus, HelpCircle } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { DateRangeSelector } from '../components/DateRangeSelector';
import NoGatewayBanner from '../components/NoGatewayBanner';

export default function DashboardOverview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  const loadData = useCallback(async (from, to) => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const [sumRes, ordRes] = await Promise.all([
        reportsAPI.summary(params),
        ordersAPI.list({ limit: 5 })
      ]);
      setSummary(sumRes.data);
      setRecentOrders(ordRes.data.orders || []);
    } catch (err) {
      // Dashboard load failed - UI handles empty state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(dateFrom, dateTo);
  }, [loadData, dateFrom, dateTo]);

  const handleRangeChange = useCallback((from, to) => {
    setDateFrom(from);
    setDateTo(to);
  }, []);

  const getRangeLabel = () => {
    if (!dateFrom && !dateTo) return 'All Time';
    if (dateFrom && dateTo) {
      return `${dateFrom} to ${dateTo}`;
    }
    return 'Custom range';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-32 rounded-xl bg-muted animate-pulse" />)}
        </div>
      </div>
    );
  }

  const kpis = [
    { label: 'Total Revenue', desc: 'Total sales amount collected from all paid orders within the selected date range.', value: `RM ${(summary?.total_revenue || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}`, icon: DollarSign, bg: '#195259', iconColor: 'text-white' },
    { label: 'Total Orders', desc: 'Number of completed orders placed by customers. Includes all payment methods.', value: summary?.total_orders || 0, icon: ShoppingCart, bg: '#FF9A00', iconColor: 'text-white' },
    { label: 'Customers', desc: 'Number of unique customers who have purchased from your store.', value: summary?.total_customers || 0, icon: Users, bg: '#195259', iconColor: 'text-white' },
    { label: 'Products', desc: 'Total number of products listed in your store, including bundles.', value: summary?.top_products?.length || 0, icon: Package, bg: '#FF9A00', iconColor: 'text-white' },
  ];

  return (
    <TooltipProvider delayDuration={200}>
    <div className="space-y-6 max-w-[1200px]">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" style={{ color: '#195259' }}>Welcome back, {(user?.username || user?.name)?.split(' ')[0]}</h1>
          <p className="text-sm text-muted-foreground mt-1">Here&apos;s what&apos;s happening with your store</p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangeSelector onRangeChange={handleRangeChange} defaultPreset="last30" />
          <Button onClick={() => navigate('/app/products')} data-testid="overview-add-product" style={{ backgroundColor: '#FF9A00', color: '#195259' }} className="font-semibold hover:opacity-90">
            <Plus className="h-4 w-4 mr-1.5" /> Add Product
          </Button>
        </div>
      </div>

      <NoGatewayBanner />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis.map((kpi, i) => {
          const Icon = kpi.icon;
          return (
            <Card key={kpi.label} className="card-hover">
              <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm text-muted-foreground">{kpi.label}</p>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/50 cursor-help hover:text-muted-foreground transition-colors" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[220px] text-xs leading-relaxed">
                          {kpi.desc}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <p className="text-2xl font-semibold mt-1" style={{ color: '#195259' }}>{kpi.value}</p>
                  </div>
                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${kpi.iconColor}`} style={{ backgroundColor: kpi.bg }}>
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Revenue Trend */}
        <Card className="lg:col-span-8">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold" style={{ color: '#195259' }}>Revenue Trend</CardTitle>
            <Badge style={{ backgroundColor: 'rgba(25,82,89,0.1)', color: '#195259' }}>{getRangeLabel()}</Badge>
          </CardHeader>
          <CardContent>
            {summary?.revenue_trend?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={summary.revenue_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(25,82,89,0.12)" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <RechartsTooltip />
                  <Area type="monotone" dataKey="revenue" stroke="hsl(187, 56%, 22%)" fill="rgba(25,82,89,0.12)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <TrendingUp className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p>No sales data yet</p>
                  <p className="text-xs mt-1">Revenue chart will appear once you make sales</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Products */}
        <Card className="lg:col-span-4">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold" style={{ color: '#195259' }}>Top Products</CardTitle>
          </CardHeader>
          <CardContent>
            {summary?.top_products?.length > 0 ? (
              <div className="space-y-4">
                {summary.top_products.map((p, i) => (
                  <div key={p.id} className="flex items-center gap-3">
                    <span className="text-sm font-mono text-muted-foreground w-5">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.total_sales || 0} sales</p>
                    </div>
                    <span className="text-sm font-semibold">RM {(p.total_revenue || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-40 flex items-center justify-center text-muted-foreground text-sm">
                No products yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Orders */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base font-semibold" style={{ color: '#195259' }}>Recent Orders</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate('/app/orders')} style={{ color: '#FF9A00' }}>
            View all <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {recentOrders.length > 0 ? (
            <div className="space-y-3">
              {recentOrders.map(order => (
                <div key={order.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{order.customer_name}</p>
                    <p className="text-xs text-muted-foreground">{order.product_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold">RM {(order.amount || 0).toFixed(2)}</p>
                    <Badge variant={order.status === 'paid' ? 'default' : 'secondary'} className="text-[10px]">{order.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-32 flex items-center justify-center text-muted-foreground text-sm">
              No orders yet. Share your store to start selling!
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </TooltipProvider>
  );
}
